import {
  Button,
  DatePicker,
  Form,
  Image,
  Input,
  Space,
  Table,
  Tag,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { GetOrderListDto, GetOrderListV2Res, getOrderListV2 } from 'apis/oms';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import { findLabelByValue } from 'commons/options';
import LinkButton from 'components/link-button';
import showTotal from 'components/show-total';
import dayjs, { Dayjs } from 'dayjs';
import i18n from 'i18n';
import { useCallback, useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import { PageQuery } from 'types/base';
import formatTime from 'utils/formatTime';
import setQueryParameters from 'utils/setQueryParameters';
import queryString from 'query-string';
import { getOrderStatusConfig } from 'constant/oms-order-status';

const PersonalOrderList = () => {
  const { RangePicker } = DatePicker;
  const [form] = Form.useForm<
    Omit<GetOrderListDto, 'createTime'> & {
      createTime: Dayjs[];
    }
  >();
  const navigate = useNavigate();

  const userInfo = useAppSelector(selectUserInfo);

  const {
    loading,
    setLoading,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    total,
    setTotal,
    dataSource,
    setDataSource,
  } = usePagination<GetOrderListV2Res['list'][number]>();
  const { orderStatusOptions } = useAppSelector(selectGlobalInfo);

  const getDataSource = useCallback(
    async ({ pageNum, pageSize }: PageQuery) => {
      const { createTime, ...otherFormData } = form.getFieldsValue();
      let [start, end] = ['', ''];
      if (createTime) {
        [start, end] = [
          dayjs(createTime[0]).startOf('day').format(),
          dayjs(createTime[1]).endOf('day').format(),
        ];
      } else {
        const now = dayjs();
        start = now.startOf('month').startOf('day').format();
        end = now.endOf('month').endOf('day').format();
      }
      const data = {
        ...otherFormData,
        start,
        end,
        pageNum,
        pageSize,
        staffName: userInfo.nickName,
      };

      setQueryParameters(data);

      try {
        setLoading(true);
        const { list, total } = await getOrderListV2(data);
        setDataSource(list);
        setTotal(total);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    },
    [form, setDataSource, setLoading, setTotal, userInfo.nickName],
  );

  useEffect(() => {
    if (!userInfo.nickName) return;
    const parsed = queryString.parse(window.location.search);

    const formData: any = { ...parsed };

    // 如果URL中没有时间参数，设置默认当月时间范围
    if (!parsed.start || !parsed.end) {
      const now = dayjs();
      const startOfMonth = now.startOf('month');
      const endOfMonth = now.endOf('month');

      parsed.start = startOfMonth.format();
      parsed.end = endOfMonth.format();

      // 设置表单的 createTime 字段
      formData.createTime = [startOfMonth, endOfMonth];
    } else {
      const startStr = Array.isArray(parsed.start)
        ? parsed.start[0]
        : parsed.start;
      const endStr = Array.isArray(parsed.end) ? parsed.end[0] : parsed.end;

      if (startStr && endStr) {
        formData.createTime = [dayjs(startStr), dayjs(endStr)];
      }
    }

    form.setFieldsValue(formData);
    const pageNum = parsed.pageNum ? +parsed.pageNum : 1;
    const pageSize = parsed.pageSize ? +parsed.pageSize : 10;
    setPageNum(pageNum);
    setPageSize(pageSize);

    getDataSource({ pageNum, pageSize });
  }, [form, getDataSource, setPageNum, setPageSize, userInfo.nickName]);

  const onFinish = async () => {
    setPageNum(1);
    getDataSource({ pageNum: 1, pageSize });
  };

  const onReset = () => {
    // 重置表单但保留当月时间范围
    const now = dayjs();
    const defaultValues = {
      createTime: [now.startOf('month'), now.endOf('month')],
    };

    form.resetFields();
    form.setFieldsValue(defaultValues);
    onFinish();
  };

  const columns: ColumnsType<GetOrderListV2Res['list'][number]> =
    useMemo(() => {
      return [
        {
          title: <Trans i18nKey={LOCALS.dwawuQNUEi}></Trans>,
          dataIndex: 'id',
          key: 'id',
          width: 80,
          render: (id: GetOrderListV2Res['list'][number]['id']) => {
            return <LinkButton href={`/oms/order-view/${id}`}>{id}</LinkButton>;
          },
        },
        {
          title: <Trans i18nKey={LOCALS.order_sn}></Trans>,
          dataIndex: 'orderSn',
          key: 'orderSn',
          width: 140,
        },
        {
          title: <Trans i18nKey={LOCALS.product_pictures}></Trans>,
          key: 'product_pictures',
          width: 150,
          render: ({ orderItems }: GetOrderListV2Res['list'][number]) => {
            return (
              <div className="flex gap-2">
                {orderItems.map(({ productPic, productId }) => {
                  return (
                    <div key={productId} className="w-16 h-16">
                      {productPic ? (
                        <Image
                          src={productPic}
                          alt="product"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        '-'
                      )}
                    </div>
                  );
                })}
              </div>
            );
          },
        },
        {
          title: <Trans i18nKey={LOCALS.sales_product_id}></Trans>,
          dataIndex: 'orderItems',
          key: 'order-id-list',
          width: 100,
          render: (
            orderItems: GetOrderListV2Res['list'][number]['orderItems'],
          ) => {
            return (
              <div>
                {orderItems.map(({ productId }) => {
                  const href = `/pms/product-view/${productId}`;
                  return (
                    <div key={productId}>
                      <a
                        href={href}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(href);
                        }}
                      >
                        {productId}
                      </a>
                    </div>
                  );
                })}
              </div>
            );
          },
        },
        {
          title: <Trans i18nKey={LOCALS.SYLYoWTeQq}></Trans>,
          dataIndex: 'orderItems',
          key: 'orderItems',
          width: 150,
          render: (
            orderItems: GetOrderListV2Res['list'][number]['orderItems'],
          ) => {
            return (
              <div>
                {orderItems.map(({ product, productId }) => {
                  const href = `/pms/product-view/${productId}`;
                  return (
                    <div key={productId}>
                      <a
                        href={href}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(href);
                        }}
                      >
                        {product.description || '-'}
                      </a>
                    </div>
                  );
                })}
              </div>
            );
          },
        },
        {
          title: <Trans i18nKey={LOCALS.pay_amount}></Trans>,
          key: 'payAmount',
          width: 150,
          render: ({
            payAmount,
            payAmountActualCurrency,
            orderItems: [{ actualCurrency }],
          }: GetOrderListV2Res['list'][number]) => {
            if (actualCurrency && payAmountActualCurrency !== null) {
              return `${actualCurrency} ${payAmountActualCurrency.toLocaleString()}`;
            }

            return `JPY ${payAmount.toLocaleString()}`;
          },
        },
        {
          title: <Trans i18nKey={LOCALS.created_time}></Trans>,
          dataIndex: 'createTime',
          key: 'createTime',
          width: 170,
          render: (
            createTime: GetOrderListV2Res['list'][number]['createTime'],
          ) => {
            return formatTime(createTime);
          },
        },
        {
          title: <Trans i18nKey={LOCALS.delivery_time}></Trans>,
          dataIndex: 'deliveryTime',
          key: 'deliveryTime',
          width: 100,
          render: (
            deliveryTime: GetOrderListV2Res['list'][number]['deliveryTime'],
          ) => {
            return formatTime(deliveryTime);
          },
        },
        {
          title: <Trans i18nKey={LOCALS.order_status}></Trans>,
          dataIndex: 'status',
          width: 120,
          key: 'status',
          fixed: 'right',
          render: (status: GetOrderListV2Res['list'][number]['status']) => {
            return (
              <Tag color={getOrderStatusConfig(status).color}>
                {findLabelByValue(status, orderStatusOptions)}
              </Tag>
            );
          },
        },
      ];
    }, [navigate, orderStatusOptions]);

  return (
    <div>
      <h2 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>
        {i18n.t(LOCALS.CXvOjSxLCk)} - {i18n.t(LOCALS.order_list)}
      </h2>

      <Form form={form} layout={'inline'} onFinish={onFinish}>
        <Form.Item label={<Trans i18nKey={LOCALS.KaLvBciebv} />} name="orderSn">
          <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
        </Form.Item>

        <Form.Item
          label={<Trans i18nKey={LOCALS.jHkqqeaSOv} />}
          name="productSn"
        >
          <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
        </Form.Item>

        <Form.Item
          label={<Trans i18nKey={LOCALS.created_time} />}
          name="createTime"
        >
          <RangePicker />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              <Trans i18nKey={LOCALS.search} />
            </Button>

            <Button htmlType="button" onClick={onReset}>
              <Trans i18nKey={LOCALS.reset} />
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        bordered
        tableLayout="fixed"
        pagination={{
          showTotal,
          total,
          pageSize,
          current: pageNum,
          onChange: (page, pageSize) => {
            setPageNum(page);
            setPageSize(pageSize);
            getDataSource({ pageNum: page, pageSize });
          },
        }}
        loading={loading}
        rowKey={'id'}
        style={{
          marginTop: 12,
        }}
        dataSource={dataSource}
        columns={columns}
      />
    </div>
  );
};

export default PersonalOrderList;
